import React, { useEffect, Suspense, lazy } from 'react';
import { AuthProvider, useAuth } from './components/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { PlayerProvider } from './components/player/PlayerContext';
import PersistentPlayer from './components/player/PersistentPlayer';
import EpisodeBuilderApp from './components/EpisodeBuilderApp';
// Brand accounts get a separate, lazily-loaded sponsor surface (kept out of
// the host bundle for the ~99% of users who aren't brands).
const SponsorApp = lazy(() => import('./components/sponsor/SponsorApp'));
import AmbientBackground from './components/layout/AmbientBackground';
import ResetPasswordPage from './pages/ResetPasswordPage';
import AdminPortal from './components/AdminPortal';
import ErrorBoundary from './components/ErrorBoundary';
import { APP_HOST, LANDING_HOST } from './lib/hosts';
import './index.css';

/* ────────────────────────────────────────────────────────────────────────────
   App root.

   Standalone routes (rendered outside the main app shell) live here so
   they work for visitors who aren't fully signed in yet — recovery
   flows, email confirmations, the admin portal, etc. The standalone
   page renders inside the AuthProvider so it can talk to Supabase, but
   skips ToastProvider and the full EpisodeBuilderApp shell.
   ──────────────────────────────────────────────────────────────────── */

function isStandalonePath(pathname) {
  return pathname === '/reset-password' || pathname === '/admin';
}

/* Supabase's OAuth + magic-link callbacks land with tokens in the URL
   hash. Don't redirect before the client has had a chance to parse
   them into a session — otherwise the redirect strips the hash and
   the user appears to sign in successfully but lands on the wrong
   surface with no session. */
function isAuthCallback() {
  if (typeof window === 'undefined') return false;
  const hash = window.location.hash || '';
  return hash.includes('access_token=')
      || hash.includes('refresh_token=')
      || hash.includes('error=');
}

/* Keep the visible host in sync with the user's auth state.

   - Signed-in visitor on the marketing apex (meetepio.com / www) →
     bounce to app.meetepio.com so the URL bar reflects "you're in
     the app." The session lives in a `.meetepio.com`-scoped cookie
     so the hop is seamless.
   - Signed-out visitor on app.meetepio.com → bounce to meetepio.com
     so the URL bar reflects "you're looking at the marketing site,"
     not the app's host with the landing page rendered.

   Skipped while auth is still loading (otherwise a fresh page load
   would briefly read user=null and bounce before the cookie session
   restores). Skipped on /reset-password and /admin so logged-out
   users can complete those flows on the app subdomain. Skipped when
   the URL hash carries OAuth/magic-link tokens so Supabase gets a
   chance to consume them. Localhost, Vercel preview URLs, and the
   legacy getepisode.ai host all skip the bounce — those don't carry
   the apex/subdomain split. */
function HostSyncRedirect() {
  const { user, sessionReady } = useAuth();
  useEffect(() => {
    // Gate on `sessionReady`, not `loading`: as soon as we know whether
    // there's a session we can bounce, without waiting for the profile /
    // role fetch that the apex→app redirect is about to throw away.
    if (!sessionReady || typeof window === 'undefined') return;
    if (isAuthCallback()) return;

    const h = (window.location.hostname || '').toLowerCase();
    const onApex = h === LANDING_HOST || h === `www.${LANDING_HOST}`;
    const onApp  = h === APP_HOST;

    if (user && onApex) {
      const target = `https://${APP_HOST}${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.location.replace(target);
      return;
    }
    if (!user && onApp && !isStandalonePath(window.location.pathname)) {
      window.location.replace(`https://${LANDING_HOST}/`);
    }
  }, [user, sessionReady]);
  return null;
}

/* Which authenticated surface to render. Brand (sponsor) accounts get their
   own dashboard, isolated from the host app; hosts + guests get the main
   EpisodeBuilderApp (guest trimming happens inside it). Falls back to the host
   app while the profile is still loading so there's no gate flicker for the
   ~99% of users who aren't brands. */
function AppSurface() {
  const { user, loading, accountType } = useAuth();
  if (user && !loading && accountType === 'brand') {
    return (
      <Suspense fallback={null}>
        <SponsorApp />
      </Suspense>
    );
  }
  return <EpisodeBuilderApp />;
}

export default function App() {
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';

  if (isStandalonePath(path)) {
    return (
      <AuthProvider>
        <HostSyncRedirect />
        {/* ToastProvider is needed here too: the admin tabs (Sponsors,
            Featured, etc.) call useToast() for save/remove feedback, and
            this standalone route renders outside the main app shell's
            provider. Without it, opening a toast-using tab throws. */}
        <ToastProvider>
          <AmbientBackground />
          <ErrorBoundary>
            {path === '/reset-password' && <ResetPasswordPage />}
            {path === '/admin'          && <AdminPortal />}
          </ErrorBoundary>
        </ToastProvider>
      </AuthProvider>
    );
  }

  return (
    <AuthProvider>
      <HostSyncRedirect />
      <ToastProvider>
        {/* Mini-spec #14: app-wide animated atmospheric layer. Mounted
            once here so the orb animations persist across route changes
            (no remount = no animation restart). The landing hero and any
            opaque section just paint over it; everything else lets it
            show through. */}
        <AmbientBackground />
        {/* PlayerProvider lives at the root so the persistent audio
            player survives every route change. The <audio> element
            inside <PersistentPlayer /> never unmounts as long as the
            user stays in-app. Mounted at root so playback keeps going
            on profile → cuts → create → settings → … flows. */}
        <PlayerProvider>
          <ErrorBoundary>
            <AppSurface />
          </ErrorBoundary>
          <PersistentPlayer />
        </PlayerProvider>
      </ToastProvider>
    </AuthProvider>
  );
}
